// ===== FILE 2: utils/constants.js =====
// COPY EVERYTHING BELOW INTO constants.js:

export const CONTENT_TYPES = {
    TEXT: 'text',
    HEADING: 'heading',
    LIST: 'list',
    INFO_BOX: 'info-box',
    EXERCISE_BOX: 'exercise-box',
    WARNING_BOX: 'warning-box',
    VIDEO: 'video',
    IMAGE: 'image',
    AUDIO: 'audio',
    CARDS: 'cards',
    STACKED_CARDS: 'stacked-cards',
    GALLERY: 'gallery',
    TABLE: 'table',
    WORKSHEET: 'worksheet' 
};

export const BOPPPS_SECTIONS = [
    {
        id: 'bridge-in',
        title: 'Bridge-In',
        type: 'boppps',
        description: 'Connect to previous learning'
    },
    {
        id: 'outcomes',
        title: 'Learning Outcomes',
        type: 'boppps',
        description: 'Define what students will achieve'
    },
    {
        id: 'pre-assessment',
        title: 'Pre-Assessment',
        type: 'boppps',
        description: 'Gauge existing knowledge'
    },
    {
        id: 'participatory-learning',
        title: 'Participatory Learning',
        type: 'boppps',
        description: 'Main content delivery'
    },
    {
        id: 'post-assessment',
        title: 'Post-Assessment',
        type: 'boppps',
        description: 'Verify learning objectives'
    },
    {
        id: 'summary',
        title: 'Summary & Next Steps',
        type: 'boppps',
        description: 'Consolidate key takeaways'
    },
    {
        id: 'resources',
        title: 'Resources & Materials',
        type: 'content',
        description: 'Additional materials and references'
    }
];

export const VIDEO_PLATFORMS = {
    YOUTUBE: 'youtube',
    VIMEO: 'vimeo',
    PANOPTO: 'panopto'
};

export const CARD_LAYOUTS = {
    '2x1': { label: '2x1 Grid', class: 'grid-cols-1 md:grid-cols-2' },
    '2x2': { label: '2x2 Grid', class: 'grid-cols-1 md:grid-cols-2' },
    '3x1': { label: '3x1 Grid', class: 'grid-cols-1 md:grid-cols-3' },
    '1x3': { label: '1x3 Vertical', class: 'grid-cols-1' }
};

export const CARD_STYLES = {
    info: {
        bg: 'bg-slate-50',
        border: 'border-l-slate-400',
        accent: 'text-slate-700',
        label: 'Info Style'
    },
    exercise: {
        bg: 'bg-emerald-50',
        border: 'border-l-emerald-400',
        accent: 'text-emerald-700',
        label: 'Exercise Style'
    },
    warning: {
        bg: 'bg-amber-50',
        border: 'border-l-amber-400',
        accent: 'text-amber-700',
        label: 'Warning Style'
    },
    success: {
        bg: 'bg-green-50',
        border: 'border-l-green-400',
        accent: 'text-green-700',
        label: 'Success Style'
    }
};

export const BOX_CONFIGS = {
    'info-box': {
        bg: 'bg-blue-50',
        border: 'border-l-4 border-blue-400',
        icon: 'AlertCircle',
        iconColor: 'text-blue-500',
        label: 'Info Box'
    },
    'exercise-box': {
        bg: 'bg-emerald-50',
        border: 'border-l-4 border-emerald-400',
        icon: 'CheckCircle',
        iconColor: 'text-emerald-500',
        label: 'Exercise Box'
    },
    'warning-box': {
        bg: 'bg-amber-50',
        border: 'border-l-4 border-amber-400',
        icon: 'AlertTriangle',
        iconColor: 'text-amber-500',
        label: 'Warning Box'
    }
};

export const IMAGE_SIZES = {
    small: { label: 'Small', class: 'max-w-xs', maxWidth: '20rem' },
    medium: { label: 'Medium', class: 'max-w-md', maxWidth: '28rem' },
    large: { label: 'Large', class: 'max-w-2xl', maxWidth: '42rem' },
    full: { label: 'Full Width', class: 'w-full', width: '100%' }
};

export const GALLERY_COLUMNS = {
    '2': { label: '2 Columns', class: 'grid-cols-1 sm:grid-cols-2' },
    '3': { label: '3 Columns', class: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' },
    '4': { label: '4 Columns', class: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4' }
};

// Auto-save Configuration
export const AUTO_SAVE_CONFIG = {
    INTERVAL: 2000, // 2 seconds
    STORAGE_PREFIX: 'lecture-template-autosave-',
    MAX_HISTORY: 10
};

// Preset Content Block Layouts (PowerPoint-style)
export const PRESET_LAYOUTS = {
    'title-slide': {
        name: 'Title Slide',
        icon: '📰',
        description: 'Large title with subtitle',
        keywords: ['title', 'cover', 'intro'],
        blocks: [
            { type: 'headline', content: '<h1 style="font-size: 3rem; text-align: center; margin: 2rem 0;">Lesson Title</h1>' },
            { type: 'text', content: '<p style="font-size: 1.5rem; text-align: center; color: #64748b;">Subtitle or brief description</p>' }
        ]
    },
    'title-and-content': {
        name: 'Title and Content',
        icon: '📄',
        description: 'Title with content area below',
        keywords: ['title', 'content', 'standard'],
        blocks: [
            { type: 'headline', content: '<h2>Section Title</h2>' },
            { type: 'text', content: '<p>Add your content here...</p>' }
        ]
    },
    'section-header': {
        name: 'Section Header',
        icon: '🏷️',
        description: 'Large section divider with title',
        keywords: ['section', 'header', 'divider'],
        blocks: [
            { type: 'divider', content: { style: 'solid', thickness: 3, color: '#3b82f6', spacing: 'medium', width: 'full' } },
            { type: 'headline', content: '<h1 style="font-size: 2.5rem; text-align: center; color: #3b82f6; margin: 1rem 0;">New Section</h1>' },
            { type: 'divider', content: { style: 'solid', thickness: 3, color: '#3b82f6', spacing: 'medium', width: 'full' } }
        ]
    },
    'two-content': {
        name: 'Two Content',
        icon: '📊',
        description: 'Title with two side-by-side content areas',
        keywords: ['two', 'columns', 'split', 'comparison'],
        blocks: [
            { type: 'headline', content: '<h2>Section Title</h2>' },
            {
                type: 'cards',
                layout: '2x1',
                style: 'info',
                items: [
                    { title: 'First Topic', content: '<p>Content goes here...</p>' },
                    { title: 'Second Topic', content: '<p>Content goes here...</p>' }
                ]
            }
        ]
    },
    'comparison': {
        name: 'Comparison',
        icon: '⚖️',
        description: 'Compare two concepts side by side',
        keywords: ['compare', 'versus', 'pros', 'cons'],
        blocks: [
            { type: 'headline', content: '<h2>Comparison</h2>' },
            {
                type: 'multi-column-cards',
                columns: [
                    {
                        title: 'Option A',
                        width: '50%',
                        cards: [
                            {
                                title: 'Advantages',
                                content: '<ul><li>Point 1</li><li>Point 2</li></ul>',
                                bgColor: '#d1fae5',
                                textColor: '#065f46'
                            }
                        ]
                    },
                    {
                        title: 'Option B',
                        width: '50%',
                        cards: [
                            {
                                title: 'Advantages',
                                content: '<ul><li>Point 1</li><li>Point 2</li></ul>',
                                bgColor: '#dbeafe',
                                textColor: '#1e40af'
                            }
                        ]
                    }
                ]
            }
        ]
    },
    'title-only': {
        name: 'Title Only',
        icon: '📋',
        description: 'Simple centered title',
        keywords: ['title', 'simple', 'minimal'],
        blocks: [
            { type: 'headline', content: '<h1 style="font-size: 2.5rem; text-align: center; margin: 3rem 0;">Title Here</h1>' }
        ]
    },
    'blank': {
        name: 'Blank',
        icon: '⬜',
        description: 'Empty content area',
        keywords: ['blank', 'empty'],
        blocks: [
            { type: 'text', content: '<p>Start adding content...</p>' }
        ]
    },
    'content-with-caption': {
        name: 'Content with Caption',
        icon: '🖼️',
        description: 'Title with content and side caption',
        keywords: ['caption', 'sidebar', 'note'],
        blocks: [
            { type: 'headline', content: '<h2>Section Title</h2>' },
            {
                type: 'stacked-cards',
                items: [
                    {
                        title: 'Main Content',
                        content: '<p>Your main content goes here...</p>',
                        sideText: 'Additional notes or caption'
                    }
                ]
            }
        ]
    },
    'picture-with-caption': {
        name: 'Picture with Caption',
        icon: '🌅',
        description: 'Title with full-width image below',
        keywords: ['image', 'photo', 'picture', 'caption'],
        blocks: [
            { type: 'headline', content: '<h2>Image Title</h2>' },
            {
                type: 'multi-column-cards',
                columns: [
                    {
                        title: '',
                        widthPercent: 100,
                        cards: [
                            {
                                title: '',
                                content: '',
                                imagePlaceholder: true,
                                image: {
                                    src: '',
                                    alt: '',
                                    caption: '',
                                    imageTitle: '',
                                    imageAuthor: '',
                                    imageSource: '',
                                    imageDate: ''
                                },
                                placeholderHeading: 'Add Image',
                                placeholderText: 'Insert a picture with caption directly into this preset slot.',
                                bgColor: '#ffffff',
                                textColor: '#1f2937',
                                borderColor: 'transparent'
                            }
                        ]
                    }
                ]
            }
        ]
    },
    'title-with-picture': {
        name: 'Title with Picture',
        icon: '🖼️',
        description: 'Side-by-side title/content and image area',
        keywords: ['title', 'image', 'picture', 'layout', 'columns'],
        blocks: [
            {
                type: 'multi-column-cards',
                columns: [
                    {
                        title: '',
                        widthPercent: 50,
                        cards: [
                            {
                                title: 'Section Title',
                                content: '<p>Add your text content here...</p><p>You can also use lists or other formatted text.</p>',
                                bgColor: '#ffffff',
                                textColor: '#1f2937',
                                borderColor: '#e5e7eb'
                            }
                        ]
                    },
                    {
                        title: '',
                        widthPercent: 50,
                        cards: [
                            {
                                title: '',
                                content: '',
                                imagePlaceholder: true,
                                image: {
                                    src: '',
                                    alt: '',
                                    caption: '',
                                    imageTitle: '',
                                    imageAuthor: '',
                                    imageSource: '',
                                    imageDate: ''
                                },
                                placeholderHeading: 'Add Image',
                                placeholderText: 'Insert an image directly into this preset slot.',
                                bgColor: '#ffffff',
                                textColor: '#1f2937',
                                borderColor: 'transparent'
                            }
                        ]
                    }
                ]
            }
        ]
    },
    'title-text-image': {
        name: 'Title / Text / Image',
        icon: '🎨',
        description: 'Title and text on left, image placeholder on right',
        keywords: ['title', 'text', 'image', 'picture', 'layout', 'slide'],
        blocks: [
            {
                type: 'multi-column-cards',
                columns: [
                    {
                        title: '',
                        widthPercent: 42,
                        cards: [
                            {
                                title: '',
                                content: '<h2 style="font-size: 1.875rem; font-weight: 600; color: #1f2937; margin-bottom: 1rem; padding: 0.5rem;">Click to add title</h2><p style="color: #6b7280; font-size: 1rem; line-height: 1.6; padding: 0.5rem;">Click to add text</p>',
                                bgColor: '#ffffff',
                                textColor: '#1f2937',
                                borderColor: 'transparent'
                            }
                        ]
                    },
                    {
                        title: '',
                        widthPercent: 58,
                        cards: [
                            {
                                title: '',
                                content: '',
                                imagePlaceholder: true,
                                image: {
                                    src: '',
                                    alt: '',
                                    caption: '',
                                    imageTitle: '',
                                    imageAuthor: '',
                                    imageSource: '',
                                    imageDate: ''
                                },
                                placeholderHeading: 'Add Image',
                                placeholderText: 'Click below to place an image in this preset layout.',
                                bgColor: '#ffffff',
                                textColor: '#1f2937',
                                borderColor: 'transparent'
                            }
                        ]
                    }
                ]
            }
        ]
    },
    'title-text-video': {
        name: 'Title / Text / Video',
        icon: '🎥',
        description: 'Title and text on the left with a 16:9 video placeholder on the right',
        keywords: ['title', 'text', 'video', 'layout', 'slide'],
        blocks: [
            {
                type: 'multi-column-cards',
                columns: [
                    {
                        title: '',
                        widthPercent: 42,
                        cards: [
                            {
                                title: '',
                                content: '<h2 style="font-size: 1.875rem; font-weight: 600; color: #1f2937; margin-bottom: 1rem; padding: 0.5rem;">Click to add title</h2><p style="color: #6b7280; font-size: 1rem; line-height: 1.6; padding: 0.5rem;">Click to add text</p>',
                                bgColor: '#ffffff',
                                textColor: '#1f2937',
                                borderColor: 'transparent'
                            }
                        ]
                    },
                    {
                        title: '',
                        widthPercent: 58,
                        cards: [
                            {
                                title: '',
                                content: '',
                                videoPlaceholder: true,
                                video: {
                                    src: '',
                                    videoUrl: '',
                                    videoPlatform: 'youtube',
                                    embedCode: '',
                                    aspectRatio: '16-9',
                                    videoTitle: '',
                                    videoAuthor: '',
                                    videoSource: '',
                                    videoDate: ''
                                },
                                placeholderHeading: 'Add Video',
                                placeholderText: 'Insert a 16:9 video directly into this preset slot.',
                                bgColor: '#ffffff',
                                textColor: '#1f2937',
                                borderColor: 'transparent'
                            }
                        ]
                    }
                ]
            }
        ]
    },
    'embedded-html': {
        name: 'Embedded HTML Layout',
        icon: '💻',
        description: 'Upload and embed HTML documents with live preview',
        keywords: ['html', 'embed', 'interactive', 'document', 'webpage'],
        blocks: [
            {
                type: 'embedded-html',
                content: '',
                title: '',
                description: '',
                fileName: null,
                fileSize: null,
                lastModified: null
            }
        ]
    },
    'single-column': {
        name: 'Single Column',
        icon: '📝',
        description: 'Full-width single column layout with heading and text',
        keywords: ['single', 'column', 'simple', 'full', 'width'],
        blocks: [
            { type: 'headline', content: '<h2 style="margin-bottom: 0.5rem;">Click to add title</h2>' },
            { type: 'text', content: '<p>Click to add your content here. This single-column layout gives you the full width of the slide for text, images, or any content block.</p>' }
        ]
    },
    'two-columns': {
        name: 'Two Columns',
        icon: '📊',
        description: 'Side-by-side two column layout for comparisons or dual content',
        keywords: ['two', 'columns', 'split', 'side', 'dual'],
        blocks: [
            {
                type: 'multi-column-cards',
                columns: [
                    {
                        title: '',
                        widthPercent: 50,
                        cards: [
                            {
                                title: '',
                                content: '<h3 style="font-weight: 600; margin-bottom: 0.75rem;">Column 1</h3><p>Click to edit this column. Add text, lists, or any content.</p>',
                                bgColor: '#ffffff',
                                textColor: '#1f2937',
                                borderColor: '#e5e7eb'
                            }
                        ]
                    },
                    {
                        title: '',
                        widthPercent: 50,
                        cards: [
                            {
                                title: '',
                                content: '<h3 style="font-weight: 600; margin-bottom: 0.75rem;">Column 2</h3><p>Click to edit this column. Add text, lists, or any content.</p>',
                                bgColor: '#ffffff',
                                textColor: '#1f2937',
                                borderColor: '#e5e7eb'
                            }
                        ]
                    }
                ]
            }
        ]
    },
    'three-columns': {
        name: 'Three Columns',
        icon: '📋',
        description: 'Three equal columns for organizing content into thirds',
        keywords: ['three', 'columns', 'triple', 'thirds', 'grid'],
        blocks: [
            {
                type: 'multi-column-cards',
                columns: [
                    {
                        title: '',
                        widthPercent: 33,
                        cards: [
                            {
                                title: '',
                                content: '<h3 style="font-weight: 600; margin-bottom: 0.75rem;">Column 1</h3><p>Click to edit this column.</p>',
                                bgColor: '#ffffff',
                                textColor: '#1f2937',
                                borderColor: '#e5e7eb'
                            }
                        ]
                    },
                    {
                        title: '',
                        widthPercent: 34,
                        cards: [
                            {
                                title: '',
                                content: '<h3 style="font-weight: 600; margin-bottom: 0.75rem;">Column 2</h3><p>Click to edit this column.</p>',
                                bgColor: '#ffffff',
                                textColor: '#1f2937',
                                borderColor: '#e5e7eb'
                            }
                        ]
                    },
                    {
                        title: '',
                        widthPercent: 33,
                        cards: [
                            {
                                title: '',
                                content: '<h3 style="font-weight: 600; margin-bottom: 0.75rem;">Column 3</h3><p>Click to edit this column.</p>',
                                bgColor: '#ffffff',
                                textColor: '#1f2937',
                                borderColor: '#e5e7eb'
                            }
                        ]
                    }
                ]
            }
        ]
    },
    'image-and-text': {
        name: 'Image + Text',
        icon: '🖼️',
        description: 'Image on the left with text content on the right',
        keywords: ['image', 'text', 'picture', 'side', 'media'],
        blocks: [
            {
                type: 'multi-column-cards',
                columns: [
                    {
                        title: '',
                        widthPercent: 45,
                        cards: [
                            {
                                title: '',
                                content: '',
                                imagePlaceholder: true,
                                image: {
                                    src: '',
                                    alt: '',
                                    caption: '',
                                    imageTitle: '',
                                    imageAuthor: '',
                                    imageSource: '',
                                    imageDate: ''
                                },
                                placeholderHeading: 'Add Image',
                                placeholderText: 'Click to insert an image into this area.',
                                bgColor: '#f0f9ff',
                                textColor: '#1f2937',
                                borderColor: 'transparent'
                            }
                        ]
                    },
                    {
                        title: '',
                        widthPercent: 55,
                        cards: [
                            {
                                title: '',
                                content: '<h3 style="font-weight: 600; margin-bottom: 0.75rem;">Content Title</h3><p>Add your text content here alongside the image. Use this layout for explaining concepts with visual support.</p><ul><li>Key point one</li><li>Key point two</li><li>Key point three</li></ul>',
                                bgColor: '#ffffff',
                                textColor: '#1f2937',
                                borderColor: '#e5e7eb'
                            }
                        ]
                    }
                ]
            }
        ]
    },
    'cards-grid': {
        name: 'Card Grid',
        icon: '📇',
        description: 'Four cards in a 2x2 grid for organized content display',
        keywords: ['cards', 'grid', 'four', 'tiles', 'boxes'],
        blocks: [
            {
                type: 'multi-column-cards',
                columns: [
                    {
                        title: '',
                        widthPercent: 50,
                        cards: [
                            {
                                title: 'Card 1',
                                content: '<p>Click to edit this card content.</p>',
                                bgColor: '#f0f9ff',
                                textColor: '#1e40af',
                                borderColor: '#bfdbfe'
                            },
                            {
                                title: 'Card 3',
                                content: '<p>Click to edit this card content.</p>',
                                bgColor: '#fef3c7',
                                textColor: '#92400e',
                                borderColor: '#fde68a'
                            }
                        ]
                    },
                    {
                        title: '',
                        widthPercent: 50,
                        cards: [
                            {
                                title: 'Card 2',
                                content: '<p>Click to edit this card content.</p>',
                                bgColor: '#ecfdf5',
                                textColor: '#065f46',
                                borderColor: '#a7f3d0'
                            },
                            {
                                title: 'Card 4',
                                content: '<p>Click to edit this card content.</p>',
                                bgColor: '#fdf2f8',
                                textColor: '#9d174d',
                                borderColor: '#fbcfe8'
                            }
                        ]
                    }
                ]
            }
        ]
    },
    'hero-section': {
        name: 'Hero Section',
        icon: '🎯',
        description: 'Large hero area with centered title for impactful slides',
        keywords: ['hero', 'banner', 'large', 'centered', 'impact'],
        blocks: [
            { type: 'headline', content: '<h1 style="font-size: 3rem; text-align: center; margin: 1.5rem 0; font-weight: 700;">Hero Title</h1>' },
            { type: 'text', content: '<p style="font-size: 1.25rem; text-align: center; color: #64748b; max-width: 600px; margin: 0 auto 2rem auto;">Add a compelling subtitle or description that draws attention to your main message.</p>' },
            { type: 'text', content: '<p style="text-align: center;"><strong style="display: inline-block; padding: 0.75rem 2rem; background: #3b82f6; color: white; border-radius: 0.5rem; font-size: 1rem;">Call to Action</strong></p>' }
        ]
    }
};

// ===== END OF constants.js =====
